import React from 'react';
import { rowFor } from '../utils/rtl';
import { StyleSheet, Text, View } from 'react-native';
import Svg, { Path } from 'react-native-svg';
import { useThemedStyles } from '../hooks/useThemedStyles';
import { arcPath, CHART_COLORS, ChartData, pieSlices, TONE_COLORS } from '../utils/workspaceChart';
import { colors, typography } from '../theme';

/** A reply table's chart: horizontal bars (optionally against a second value) or a donut with its legend. */
export function WorkspaceChart({ data, isRTL, format }: { data: ChartData; isRTL: boolean; format: (n: number) => string }) {
  const styles = useThemedStyles(getStyles);
  const row = { flexDirection: rowFor(isRTL) };
  const align = { textAlign: isRTL ? 'right' as const : 'left' as const };
  if (data.type === 'pie') {
    const slices = pieSlices(data.items), size = 132, c = size / 2;
    return <View style={styles.box}>
      <Text style={[styles.title, align]}>{data.title}</Text>
      <View style={[styles.pieRow, row]}>
        <Svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
          {slices.map((s, i) => <Path key={i} d={arcPath(c, c, c * 0.56, s.start, s.end)} fill={s.color} />)}
        </Svg>
        <View style={styles.legend}>
          {slices.map((s, i) => <View key={i} style={[styles.legendRow, row]}>
            <View style={[styles.swatch, { backgroundColor: s.color }]} />
            <Text style={[styles.legendText, align]} numberOfLines={1}>{s.label}</Text>
            <Text style={styles.legendValue}>{format(s.value)} · {s.percent}%</Text>
          </View>)}
        </View>
      </View>
    </View>;
  }
  const compared = data.items.some(i => i.compare !== undefined);
  // At most 82% of the line: the value is written after the bar.
  const pct = (v?: number) => `${data.max && v ? Math.max(1.5, (v / data.max) * 82) : 0}%` as const;
  return <View style={styles.box}>
    <Text style={[styles.title, align]}>{data.title}</Text>
    {data.items.map((item, i) => <View key={i} style={[styles.barRow, row]}>
      <Text style={[styles.barLabel, align]} numberOfLines={2}>{item.label}</Text>
      <View style={styles.barArea}>
        <View style={[styles.barLine, row]}>
          <View style={[styles.bar, { width: pct(item.value), backgroundColor: item.tone ? TONE_COLORS[item.tone] : CHART_COLORS[0] }]} />
          <Text style={styles.barValue}>{format(item.value)}</Text>
        </View>
        {item.compare !== undefined && <View style={[styles.barLine, row]}>
          <View style={[styles.bar, styles.compareBar, { width: pct(item.compare) }]} />
          <Text style={[styles.barValue, styles.compareValue]}>{format(item.compare)}</Text>
        </View>}
      </View>
    </View>)}
    {compared && <View style={[styles.keyRow, row]}>
      <View style={[styles.swatch, { backgroundColor: CHART_COLORS[0] }]} /><Text style={styles.keyText}>{data.valueLabel}</Text>
      <View style={[styles.swatch, styles.compareBar]} /><Text style={styles.keyText}>{data.compareLabel}</Text>
    </View>}
  </View>;
}

const getStyles = () => StyleSheet.create({
  box: { gap: 8, paddingHorizontal: 12, paddingVertical: 10, borderTopWidth: 1, borderTopColor: colors.border },
  title: { fontSize: 12, fontFamily: typography.fontFamily.semibold, color: colors.text.secondary },
  pieRow: { alignItems: 'center', gap: 14, flexWrap: 'wrap' },
  legend: { flex: 1, minWidth: 150, gap: 5 },
  legendRow: { alignItems: 'center', gap: 6 },
  legendText: { flex: 1, fontSize: 12, color: colors.text.primary },
  legendValue: { fontSize: 11.5, color: colors.text.secondary, writingDirection: 'ltr' },
  swatch: { width: 10, height: 10, borderRadius: 3 },
  barRow: { alignItems: 'center', gap: 8 },
  barLabel: { width: '34%', fontSize: 11.5, lineHeight: 15, color: colors.text.primary },
  barArea: { flex: 1, gap: 3 },
  barLine: { alignItems: 'center', gap: 6 },
  bar: { height: 12, borderRadius: 4 },
  compareBar: { backgroundColor: '#94a3b8' },
  barValue: { fontSize: 11, color: colors.text.primary, writingDirection: 'ltr', fontVariant: ['tabular-nums'] },
  compareValue: { color: colors.text.secondary },
  keyRow: { alignItems: 'center', gap: 6, flexWrap: 'wrap' },
  keyText: { fontSize: 11, color: colors.text.secondary, marginEnd: 8 },
});
